import * as React from "react";
import { styled } from "@mui/material/styles";
import { useDropzone } from "react-dropzone";
import Box from "@mui/material/Box";
import defaultImage from "./../../assets/auth/defaultImage.png";
import {
    InputLabel,
    Typography,
    CircularProgress,
    IconButton,
    Tooltip,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import DeleteIcon from "@mui/icons-material/Delete";
import i18next from "i18next";
import useDeleteMedia from "../../hooks/media/useDeleteMedia";

const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
});

export default function InputFileUpload({
    labelImage,
    labelBioImage,
    width = 120,
    height,
    borderRadius = "50%",
    onChange,
    value,
    name = "logo",
    required,
    isUploading = false,
    errorMessage = "",
    successMessage = "",
    mediaId,
    onDeleted,
    showDelete = true, // اختياري: للتحكم في إظهار زر الحذف
    objectFit = "scale-down",
}) {
    const theme = useTheme();
    const lng = i18next.language;

    const { deleteSingleMedia, isDeletingSingle } = useDeleteMedia();

    const [hasInteracted, setHasInteracted] = React.useState(false);
    const [imageUrl, setImageUrl] = React.useState(value || defaultImage);

    React.useEffect(() => {
        setImageUrl(value || defaultImage);
    }, [value]);

    const clearLocally = React.useCallback(() => {
        setImageUrl(defaultImage);
        setHasInteracted(false);

        // تفريغ قيمة الفورم/الحقل (لو تستخدمها كرابط)
        onChange?.({ target: { name, value: "" } });

        // ملاحظة: لا يمكن فعليًا "مسح" FileList بشكل موثوق،
        // لكن هذا يكفي غالبًا لإفراغ قيمة الحقل في الفورم.
    }, [name, onChange]);

    const handleDelete = (e) => {
        e.preventDefault();
        e.stopPropagation();

        // لو ما في id (قبل الرفع): فقط امسح المعاينة محليًا
        if (!mediaId) {
            clearLocally();
            onDeleted?.();
            return;
        }

        deleteSingleMedia(mediaId, {
            onSuccess: () => {
                clearLocally();
                onDeleted?.();
            },
        });
    };

    const handleFile = (file) => {
        const reader = new FileReader();
        reader.onload = () => setImageUrl(reader.result);
        reader.readAsDataURL(file);

        setHasInteracted(true);

        if (onChange) {
            const syntheticEvent = {
                target: {
                    name,
                    files: [file],
                },
            };
            onChange(syntheticEvent);
        }
    };

    const onDrop = React.useCallback(
        (acceptedFiles) => {
            const file = acceptedFiles?.[0];
            if (file) handleFile(file);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [name, onChange]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { "image/*": [".png", ".jpg", ".jpeg", ".webp", ".gif", ".avif"] },
        multiple: false,
        noClick: true,
        noKeyboard: true,
    });

    const showError = hasInteracted && Boolean(errorMessage);
    const showSuccess = hasInteracted && Boolean(successMessage) && !showError;

    const canShowDelete =
        showDelete && imageUrl && imageUrl !== defaultImage && !isUploading;

    return (
        <>
            <Box
                {...getRootProps()}
                component="label"
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    width,
                    height,
                    borderRadius,
                    overflow: "hidden",
                    backgroundColor: (t) => t.palette.background.file,
                    position: "relative",
                    border: `1px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider
                        }`,
                    transition: "border-color 0.2s ease, background-color 0.2s ease",
                    "&:hover": {
                        backgroundColor: (t) =>
                            t.palette.mode === "dark"
                                ? "rgba(255,255,255,0.04)"
                                : "rgba(0,0,0,0.02)",
                    },
                }}
            >
                <img
                    src={imageUrl || defaultImage}
                    alt="avatar"
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: objectFit,
                        opacity: isUploading ? 0.5 : 1,
                        transition: "opacity 0.2s ease",
                    }}
                />

                <VisuallyHiddenInput {...getInputProps()} name={name} required={required} />

                {/* زر حذف الصورة (Overlay) */}
                {canShowDelete && (
                    <Tooltip title={lng === "ar" ? "حذف الصورة" : "Delete image"}>
                        <span>
                            <IconButton
                                onClick={handleDelete}
                                disabled={isDeletingSingle}
                                size="small"
                                sx={{
                                    position: "absolute",
                                    top: 6,
                                    right: 6,
                                    zIndex: 5,
                                    width: 28,
                                    height: 28,
                                    bgcolor: "rgba(0,0,0,0.55)",
                                    color: "#fff",
                                    backdropFilter: "blur(2px)",
                                    "&:hover": {
                                        bgcolor: "rgba(244,67,54,0.9)",
                                    },
                                }}
                            >
                                {isDeletingSingle ? (
                                    <CircularProgress size={16} thickness={5} sx={{ color: "#fff" }} />
                                ) : (
                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                )}
                            </IconButton>
                        </span>
                    </Tooltip>
                )}

                {/* طبقة حالة السحب */}
                {isDragActive && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            bgcolor: "rgba(0,0,0,0.35)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontSize: 12,
                            textAlign: "center",
                            px: 1,
                            zIndex: 4,
                        }}
                    >
                        {lng === "ar"
                            ? "إفلت الصورة هنا للرفع"
                            : "Drop the image here to upload"}
                    </Box>
                )}

                {/* طبقة التحميل */}
                {isUploading && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            bgcolor: "rgba(0,0,0,0.45)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                            color: "#fff",
                            fontSize: 12,
                            zIndex: 4,
                        }}
                    >
                        <CircularProgress size={26} thickness={4} />
                        <Typography variant="caption">
                            {lng === "ar" ? "جاري رفع الصورة..." : "Uploading the image..."}
                        </Typography>
                    </Box>
                )}

                {/* حالة الخطأ */}
                {showError && !isUploading && (
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 6,
                            right: 6,
                            bgcolor: "rgba(244, 67, 54, 0.9)",
                            borderRadius: 999,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1.2,
                            py: 0.2,
                            zIndex: 4,
                        }}
                    >
                        <ErrorIcon sx={{ fontSize: 14, color: "#fff" }} />
                        <Typography variant="caption" sx={{ color: "#fff", maxWidth: 140 }}>
                            {lng === "ar" ? "فشل الرفع" : "Failed"}
                        </Typography>
                    </Box>
                )}

                {/* حالة النجاح */}
                {showSuccess && !isUploading && (
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 6,
                            right: 6,
                            bgcolor: "rgba(46, 125, 50, 0.9)",
                            borderRadius: 999,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1.2,
                            py: 0.2,
                            zIndex: 4,
                        }}
                    >
                        <CheckCircleIcon sx={{ fontSize: 14, color: "#fff" }} />
                        <Typography variant="caption" sx={{ color: "#fff", maxWidth: 140 }}>
                            {lng === "ar" ? "تم الرفع" : "Successful"}
                        </Typography>
                    </Box>
                )}
            </Box>

            <Box mt={1}>
                <InputLabel
                    htmlFor={name}
                    sx={{
                        mt: 1,
                        fontWeight: "bold",
                        color: theme.palette.primary.default,
                    }}
                >
                    {labelImage}
                </InputLabel>

                {labelBioImage && (
                    <Typography sx={{ fontWeight: "bold", color: "#888" }} variant="caption">
                        {labelBioImage}
                    </Typography>
                )}
            </Box>

            {/* رسائل الخطأ/النجاح */}
            {showError && (
                <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: "block" }}
                >
                    {errorMessage}
                </Typography>
            )}

            {showSuccess && (
                <Typography
                    variant="caption"
                    color="success.main"
                    sx={{ mt: 0.5, display: "block" }}
                >
                    {successMessage}
                </Typography>
            )}
        </>
    );
}
